import React, { useState, useEffect } from "react";
import "./chat-select.component.scoped.scss";
import { Box, Typography, Button } from "@material-ui/core";
import { Check } from "@material-ui/icons";
import ChatSelectButton from "../chat-select-button/chat-select-button.component";
import { useTranslation } from "react-i18next";

interface Option {
  id: string;
  name: string;
  iconKey?: string;
  description?: string;
}

interface ChatSelectProps {
  questionId: string;
  options: Option[];
  isMultiple: boolean;
  onAnswer: (questionId: string, answers: string[]) => void;
  disabled?: boolean;
  selectedAnswers?: string[];
}

const ChatSelect: React.FC<ChatSelectProps> = ({
  questionId,
  options,
  isMultiple,
  onAnswer,
  disabled = false,
  selectedAnswers = [],
}) => {
  const { t } = useTranslation();
  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    selectedAnswers,
  );
  const [isDoneClicked, setIsDoneClicked] = useState(false);

  // Reset local selection when questionId changes or selectedAnswers changes (e.g., when editing)
  useEffect(() => {
    setSelectedOptions(selectedAnswers);
    // Only reset isDoneClicked if starting a new selection (selectedAnswers is empty)
    if (selectedAnswers.length === 0) {
      setIsDoneClicked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId, selectedAnswers.join(",")]);

  const handleOptionSelect = (option: Option) => {
    // Don't allow selection if done button was clicked
    if (isDoneClicked) return;

    if (isMultiple) {
      setSelectedOptions((prev) => {
        if (prev.includes(option.id)) {
          return prev.filter((item) => item !== option.id);
        } else {
          return [...prev, option.id];
        }
      });
      // Always reset done clicked state when selection changes
      setIsDoneClicked(false);
    } else {
      // Single selection - immediately submit the answer
      onAnswer(questionId, [option.id]);
    }
  };

  const handleDoneClick = () => {
    if (selectedOptions.length > 0 && !isDoneClicked) {
      setIsDoneClicked(true);
      onAnswer(questionId, selectedOptions);
    }
  };

  const isOptionSelected = (option: Option) => {
    // Use selectedAnswers prop if provided, otherwise use internal state
    const currentSelected =
      selectedAnswers.length > 0 ? selectedAnswers : selectedOptions;
    return currentSelected.includes(option.id);
  };

  const showDoneButton = isMultiple && selectedOptions.length > 0;

  return (
    <Box className="chat-select-container">
      <Typography className="chat-select-title" variant="subtitle2">
        {isMultiple
          ? t("CHATBOT.SELECT_MULTIPLE_OPTIONS")
          : t("CHATBOT.SELECT_SINGLE_OPTION")}
      </Typography>

      <Box className="chat-select-options">
        {options.map((option, index) => (
          <ChatSelectButton
            key={index}
            option={option}
            isSelected={isOptionSelected(option)}
            isMultiple={isMultiple}
            onSelect={() => handleOptionSelect(option)}
            disabled={disabled || isDoneClicked}
            faded={disabled || isDoneClicked}
          />
        ))}
      </Box>
      {showDoneButton && (
        <Box className="chat-select-options">
          <Box className="chat-select-done">
            <Button
              variant="outlined"
              onClick={handleDoneClick}
              disabled={disabled || isDoneClicked}
              startIcon={<Check />}
              className={`done-button ${isDoneClicked ? "selected" : ""}`}
            >
              {t("CHATBOT.DONE")}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ChatSelect;
